/**
 * DashboardLayout — shell for the authenticated Creator Dashboard.
 *
 * Professional left sidebar with icon + label navigation,
 * a top bar with creator name, search, notification bell,
 * and account menu. Responsive: sidebar collapses to a
 * slide-over on mobile.
 *
 * All data comes from the authenticated user's real profile.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Menu, X, LogOut, ChevronDown,
  LayoutDashboard, User, Users, Calendar, FileText,
  BarChart3, Bell, Settings,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/auth';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Avatar } from '@/components/ui/Avatar';
import { Input } from '@/components/ui/Input';
import { DASHBOARD_NAV_ITEMS } from '@/config/dashboard';
import { ROUTES } from '@/app/routes/paths';
import { NotificationCenter } from '@/components/notification';
import { BrandLogo } from '@/components/layout';
import { getSupabase } from '@/lib/supabase';
import type { CreatorProfileRow } from '@/types/database';

const ICON_MAP: Record<string, typeof LayoutDashboard> = {
  LayoutDashboard, User, Users, Calendar, FileText, BarChart3, Bell, Settings,
};

export function DashboardLayout(): JSX.Element {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profile, setProfile] = useState<CreatorProfileRow | null>(null);

  const mobileNavRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useScrollLock(mobileNavOpen);
  useFocusTrap(mobileNavRef, mobileNavOpen);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const supabase = getSupabase();
      const { data } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!cancelled) setProfile(data as CreatorProfileRow | null);
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    setMobileNavOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escapeHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escapeHandler);
    };
  }, [userMenuOpen]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate(ROUTES.HOME);
  }, [signOut, navigate]);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-all duration-fast ease-standard',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      isActive
        ? 'bg-primary-50 text-primary-800 font-medium'
        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
    );

  const userName = profile?.display_name || profile?.full_name || user?.fullName || user?.email || 'Creator';
  const firstName = userName.split(' ')[0];

  const SidebarContent = () => (
    <>
      <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-border px-6">
        <BrandLogo className="h-7 shrink-0" />
        <span className="text-sm font-semibold tracking-tight text-foreground">Creator Hub</span>
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" aria-label="Dashboard menu">
        {DASHBOARD_NAV_ITEMS.map((item) => {
          const Icon = ICON_MAP[item.icon] ?? LayoutDashboard;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/dashboard'}
              className={navLinkClass}
            >
              <Icon size={16} aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="shrink-0 border-t border-border p-4">
        <button
          onClick={handleSignOut}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-neutral-600',
            'transition-all duration-fast ease-standard hover:bg-neutral-100 hover:text-neutral-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30',
          )}
        >
          <LogOut size={16} aria-hidden="true" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-surface-secondary">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-primary-700 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-64 shrink-0 flex-col border-r border-border bg-surface lg:flex"
        aria-label="Dashboard navigation"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Slide-Over */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Dashboard navigation menu">
          <div className="absolute inset-0 bg-overlay" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
          <div
            ref={mobileNavRef}
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-surface shadow-xl animate-slide-in-right"
          >
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-6">
              <div className="flex items-center gap-2.5">
                <BrandLogo className="h-7 shrink-0" />
                <span className="text-sm font-semibold tracking-tight text-foreground">Creator Hub</span>
              </div>
              <button
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation menu"
                className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" aria-label="Dashboard menu">
              {DASHBOARD_NAV_ITEMS.map((item) => {
                const Icon = ICON_MAP[item.icon] ?? LayoutDashboard;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/dashboard'}
                    className={navLinkClass}
                  >
                    <Icon size={16} aria-hidden="true" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
            <div className="shrink-0 border-t border-border p-4">
              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <LogOut size={16} aria-hidden="true" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-border bg-surface px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={mobileNavOpen}
              className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30 lg:hidden"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            {/* Creator name + profile photo */}
            <div className="flex items-center gap-2.5">
              <Avatar name={userName} size="sm" />
              <div className="hidden flex-col sm:flex">
                <span className="text-sm font-medium leading-tight text-foreground">{firstName}</span>
                <span className="text-xs leading-tight text-muted-foreground">Creator</span>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="hidden flex-1 max-w-xs md:block">
            <Input
              type="search"
              placeholder="Search..."
              aria-label="Search dashboard"
              className="h-9"
            />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <NotificationCenter />

            {/* Account menu */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-label="Open account menu"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                className="flex h-11 items-center gap-2 rounded-md py-1 pl-1 pr-2 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <Avatar name={userName} size="sm" />
                <ChevronDown size={14} className="text-neutral-400" aria-hidden="true" />
              </button>
              {userMenuOpen && (
                <div
                  role="menu"
                  aria-label="Account menu"
                  className="absolute right-0 top-12 w-56 rounded-lg border border-border bg-surface py-2 shadow-lg animate-scale-in"
                >
                  <div className="border-b border-border-subtle px-3 py-2">
                    <p className="truncate text-sm font-medium text-foreground">{userName}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <Link
                    to={ROUTES.DASHBOARD_PROFILE}
                    role="menuitem"
                    className="block px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                  >
                    Edit Profile
                  </Link>
                  <Link
                    to={ROUTES.DASHBOARD_SETTINGS}
                    role="menuitem"
                    className="block px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                  >
                    <LogOut size={14} aria-hidden="true" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main id="main-content" className="flex-1 overflow-y-auto p-4 lg:p-6" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
